import type { TraceResult } from '../../types/topology.ts'

/** One breadcrumb chip. ``navTo`` is the onFeatureNavigate payload for entity
 *  chips (Transformer/Feeder/Substation); null for the meter/device chips, which
 *  do not have a rich detail view. ``isOpen`` flags a normally-open device. */
export interface TraceChip {
  key: string
  label: string
  color: string
  navTo: { _type: string; id: number } | null
  isOpen: boolean
}

const COLORS = {
  meter: '#22c55e',
  device: '#a855f7',
  deviceOpen: '#ef4444',
  transformer: '#f59e0b',
  feeder: '#06b6d4',
  substation: '#ef4444',
  secondary: '#8b5cf6',
}

/** Build the ordered chips meter -> [secondary] -> xfmr -> devices -> feeder ->
 *  sub. The devices come from transformer_upstream_devices, which are UPSTREAM
 *  (source-side) of the transformer with sequence 1 = nearest upstream of it —
 *  so in meter->source order they sit BETWEEN the transformer and the
 *  feeder/substation, never between the meter and the transformer. Pure. */
export function buildTraceChips(trace: TraceResult): TraceChip[] {
  const chips: TraceChip[] = []
  chips.push({
    key: 'meter', label: 'Meter', color: COLORS.meter, navTo: null, isOpen: false,
  })
  if (trace.secondary?.available) {
    chips.push({
      key: 'secondary', label: 'Secondary', color: COLORS.secondary,
      navTo: null, isOpen: false,
    })
  }
  if (trace.transformer) {
    chips.push({
      key: 'transformer',
      label: trace.transformer.name || `Xfmr #${trace.transformer.id}`,
      color: COLORS.transformer,
      navTo: { _type: 'Transformer', id: trace.transformer.id },
      isOpen: false,
    })
  }
  for (const d of trace.devices) {
    chips.push({
      key: `device-${d.id}`,
      label: d.name || `Device #${d.id}`,
      color: d.is_open ? COLORS.deviceOpen : COLORS.device,
      navTo: null,
      isOpen: d.is_open,
    })
  }
  if (trace.feeder) {
    chips.push({
      key: 'feeder',
      label: trace.feeder.name || trace.feeder.code || `Feeder #${trace.feeder.id}`,
      color: COLORS.feeder,
      navTo: { _type: 'Feeder', id: trace.feeder.id },
      isOpen: false,
    })
  }
  if (trace.substation) {
    chips.push({
      key: 'substation',
      label: trace.substation.name || `Sub #${trace.substation.id}`,
      color: COLORS.substation,
      navTo: { _type: 'Substation', id: trace.substation.id },
      isOpen: false,
    })
  }
  return chips
}
